// Counter Intelligence — follow-up resolution against a pending action.
//
// The drawer persists visible chat messages but the classifier only ever sees the
// latest command, so short follow-ups ("yes", "order template", "combined") lost
// the intent the previous assistant question established. This module keeps a
// small structured "pending context" and resolves those follow-ups deterministically
// into a complete template-create command — no extra classifier round-trip, no
// re-clarification.
//
// Pure (no React / no I/O) so it is unit-testable.

const PENDING_TTL_MS = 30 * 60 * 1000; // 30 minutes

const CANCEL_RE = /\b(never ?mind|nevermind|cancel|stop|forget it|no thanks|not now|drop it)\b/i;
const AFFIRM_RE = /\b(yes|yep|yeah|yup|sure|ok|okay|please do|do it|do that|create it|make it|build it|go ahead|sounds good|that one|please create|let'?s do it)\b/i;
const COMBINED_RE = /\b(combined|one combined|single template|just one|one template)\b/i;
const SEPARATE_RE = /\b(separate|split|by vendor|per vendor|each vendor)\b/i;
const ORDERING_RE = /\b(order(ing)? template|order template|ordering)\b/i;
const EOP_RE = /\b(end of period|eop|period end)\b/i;
// A reply that is really a new how-to / question rather than an answer to the
// pending clarification — used to bail out of an in-progress create thread.
const PIVOT_RE = /\b(how (?:do|can|to|does|would)|where (?:do|can|is|are)|why (?:can|do|does|is)|what (?:is|are|do|does)|what'?s)\b/i;

// ── Template guidelines (how Counter Intelligence shapes a new template) ──────
// End of Period = a full count of EVERY item in the store to value goods on hand
//   at month end; assuming "all active items" is correct and expected.
// Ordering = how the store actually places orders — almost always one vendor at
//   a time (sometimes a few vendors for a given day, or a category like "all the
//   liquor"). A whole-catalogue order template is rarely what anyone wants, so
//   instead of defaulting to "all items" we ask how to divide it.
const EOP_HINT_RE = /\b(end of period|eop|period end|month[- ]?end|end of (the )?month|goods on hand|value of (goods|stock|inventory)|full (inventory )?count|count everything)\b/i;
// Anchored: matches a reply that is ONLY "all items" / "everything" with no
// qualifier — so "everything from Sysco" or "all the wine" do NOT match.
const ALL_ITEMS_ONLY_RE = /^[\s.,!]*((all|every)( of)?( the| my)? items?|all of (them|it)|everything|every item|the (whole|entire) (inventory|catalog(ue)?|database)|my (whole |entire )?(inventory|catalog(ue)?|database))[\s.,!]*$/i;
const BY_VENDOR_RE = /\b(by vendor|per vendor|each vendor|one vendor|by supplier|per supplier|vendor by vendor)\b/i;
const BY_CATEGORY_RE = /\b(by category|by categories|per category|by type|by product type|category)\b/i;
const ALL_IN_ONE_RE = /\b(all (items |of them )?in one|one (single )?template|single template|all in one|everything (in|together)|combined?)\b/i;

const ORDER_DIVISION_REPLY = [
  'Order templates work best when they match how you actually place orders — most often one vendor at a time (we can combine or split later). How should I divide this?',
  '',
  'Tip: build each template around when you order — you’ll count these items right before sending that order.',
].join('\n');

const ORDER_DIVISION_CHOICES = [
  { key: 'by_vendor', label: 'By vendor (recommended)' },
  { key: 'by_category', label: 'By category (e.g. liquor)' },
  { key: 'all_in_one', label: 'All items in one' },
  { key: 'cancel', label: 'Never mind' },
];

const TEMPLATE_CREATE_INTENTS = new Set(['templates', 'pars', 'counting', 'ordering']);

function isEopCommand(text) { return EOP_HINT_RE.test(String(text || '')); }
function isOrderingPending(pending) {
  if (pending && pending.template_type === 'end_of_period') return false;
  if (pending && pending.template_type === 'ordering') return true;
  return !isEopCommand(`${pending?.accumulated_command || ''} ${pending?.original_user_message || ''}`);
}

/**
 * Quick-reply chips for an executor "what items?" clarification — type-aware:
 * End of Period offers "all active items"; ordering steers toward dividing by
 * vendor or category instead of dumping the whole catalogue.
 */
export function clarifyScopeChoices(pending) {
  if (pending && pending.template_type === 'end_of_period') {
    return [{ key: 'all_items', label: 'All active items' }];
  }
  return [
    { key: 'by_vendor', label: 'By vendor (recommended)' },
    { key: 'by_category', label: 'By category' },
  ];
}

function askDivision(pending) {
  return {
    action: 'ask',
    reply: ORDER_DIVISION_REPLY,
    pending: { ...pending, awaiting: 'order_division', choices: ORDER_DIVISION_CHOICES, expires_at: plusMs(PENDING_TTL_MS) },
  };
}
function askVendor(pending) {
  return {
    action: 'ask',
    reply: 'Which vendor should I start with? Name one and I’ll build that order template — we can do the next vendor after.',
    pending: { ...pending, awaiting: 'vendor_choice', choices: undefined, expires_at: plusMs(PENDING_TTL_MS) },
  };
}
function askCategory(pending) {
  return {
    action: 'ask',
    reply: 'Which category? For example: liquor, wine, beer, or produce.',
    pending: { ...pending, awaiting: 'category_choice', choices: undefined, expires_at: plusMs(PENDING_TTL_MS) },
  };
}

function nowIso() { return new Date().toISOString(); }
function plusMs(ms) { return new Date(Date.now() + ms).toISOString(); }

export function isExpired(pending, now = Date.now()) {
  if (!pending || !pending.expires_at) return true;
  const t = Date.parse(pending.expires_at);
  return !Number.isFinite(t) || t <= now;
}

export function isCancel(text) {
  return CANCEL_RE.test(String(text || ''));
}

// A reply that is clearly a NEW instruction to act (not the answer to a discussion
// question). Used to bail out of a support-discussion thread when the user pivots to
// "create a template" / "delete this item" instead of answering the assistant's
// question. Deliberately narrow — an answer like "Quick service Mexican" or "a bar
// and a kitchen" has no action verb, so the discussion continues.
const NEW_COMMAND_RE = /\b(create|build|make|generate|duplicate|add|import|upload|edit|change|update|set|delete|remove|archive|convert|move|copy|rename|assign|reassign|find|clean|de-?dupe|dedupe|combine|split)\b/i;
export function isLikelyNewCommand(text) {
  return NEW_COMMAND_RE.test(String(text || ''));
}

// A reply that is genuinely just a short ANSWER to the assistant's question (e.g.
// "Quick service Mexican" after "what kind of restaurant are you?") — as opposed to a
// new request or a topic change. Only these continue a support discussion; anything
// that looks like a fresh question/command falls through to normal classification, so
// a stale discussion pending can never hijack a new request (e.g. "Can you check for
// duplicates in my catalog?").
const REQUEST_OR_QUESTION_RE = /\b(can you|could you|would you|will you|please|how (do|can|to|does)|why|what|where|when|which|who|find|check|scan|clean|remove|delete|archive|create|make|build|add|show|list|update|set|change|edit|convert|do it|help)\b/i;
export function isPlainShortAnswer(text) {
  const t = String(text || '').trim();
  if (!t) return false;
  if (t.endsWith('?')) return false;
  if (REQUEST_OR_QUESTION_RE.test(t)) return false;
  return t.split(/\s+/).length <= 6;
}

/**
 * When a support (how-to) answer ends with a question, hold a lightweight pending so
 * the user's next plain reply is understood as CONTINUING that discussion — answered
 * in context — instead of being cold-classified into a clarify dead end. Returns null
 * when the answer didn't ask anything (nothing to follow up on).
 */
export function buildSupportFollowupPending({ text, reply, intent, needsSetupContext } = {}) {
  if (!/\?\s*$/.test(String(reply || '').trim())) return null;
  return {
    kind: 'pending_support_followup',
    intent: intent || 'general',
    needs_setup_context: !!needsSetupContext,
    original_user_message: String(text || ''),
    assistant_question: String(reply || ''),
    created_at: nowIso(),
    expires_at: plusMs(PENDING_TTL_MS),
  };
}

/**
 * Build a pending context from a support (how-to) answer that ended with a
 * question and could lead to creating a template. Returns null when the intent
 * isn't a template-creation topic.
 */
export function buildPendingFromSupport({ text, reply, supportIntent }) {
  const isEop = supportIntent === 'eop';
  if (!isEop && !TEMPLATE_CREATE_INTENTS.has(supportIntent)) return null;
  if (!/\?\s*$/.test(String(reply || '').trim())) return null; // only when the assistant asked something

  const templateType = isEop ? 'end_of_period' : 'ordering';
  const grouping = COMBINED_RE.test(reply) || SEPARATE_RE.test(reply);
  return {
    kind: 'pending_template_create',
    route: 'template_create',
    template_type: templateType,
    original_user_message: text,
    assistant_question: reply,
    awaiting: grouping ? 'grouping_choice' : 'confirmation',
    choices: grouping
      ? [
          { key: 'combined', label: 'One combined template' },
          { key: 'separate_by_vendor', label: 'Separate by vendor' },
        ]
      : [
          { key: 'confirm', label: isEop ? 'Yes, create the End of Period template' : 'Yes, create it' },
          { key: 'cancel', label: 'No thanks' },
        ],
    suggested_default: grouping ? 'combined' : 'confirm',
    created_at: nowIso(),
    expires_at: plusMs(PENDING_TTL_MS),
  };
}

/**
 * Build a pending context from a Setup Review recommended next step (when that
 * step is to create an order or End of Period template).
 */
export function buildPendingFromSetup({ text, payload }) {
  const rec = payload && payload.recommended_next_step;
  if (!rec || !rec.key) return null;
  const steps = (payload && payload.steps) || [];
  if (rec.key === 'missing_order_templates') {
    const step = steps.find((s) => s && s.key === 'missing_order_templates');
    const vendor = step && Array.isArray(step.vendors) ? step.vendors[0] : null;
    return {
      kind: 'pending_setup_action', route: 'template_create', template_type: 'ordering',
      vendor: vendor || null, original_user_message: text, assistant_question: rec.question || '',
      awaiting: 'confirmation',
      choices: [
        ...(vendor ? [{ key: 'confirm', label: `Create the ${vendor} order template` }] : [{ key: 'confirm', label: 'Create it' }]),
        { key: 'eop', label: 'End of Period template instead' },
        { key: 'cancel', label: 'Not now' },
      ],
      suggested_default: 'confirm',
      created_at: nowIso(), expires_at: plusMs(PENDING_TTL_MS),
    };
  }
  if (rec.key === 'missing_eop_template') {
    return {
      kind: 'pending_setup_action', route: 'template_create', template_type: 'end_of_period',
      include: 'all_items', original_user_message: text, assistant_question: rec.question || '',
      awaiting: 'confirmation',
      choices: [
        { key: 'confirm', label: 'Create the End of Period template' },
        { key: 'cancel', label: 'Not now' },
      ],
      suggested_default: 'confirm',
      created_at: nowIso(), expires_at: plusMs(PENDING_TTL_MS),
    };
  }
  return null;
}

/**
 * Build a pending context when the template-create executor itself asks for more
 * detail ("What items should this template include?", "Red or white wine?"). The
 * user's next reply is the missing scope, accumulated onto the create command so
 * it is built rather than re-classified from scratch (which dropped the intent
 * and re-asked "create / edit / learn?").
 */
export function buildPendingTemplateClarify({ command, question }) {
  const cmd = String(command || '').trim();
  return {
    kind: 'pending_template_create',
    route: 'template_create',
    awaiting: 'item_scope',
    template_type: isEopCommand(cmd) ? 'end_of_period' : 'ordering',
    accumulated_command: cmd,
    original_user_message: cmd,
    assistant_question: question || '',
    created_at: nowIso(),
    expires_at: plusMs(PENDING_TTL_MS),
  };
}

// Match a loose vendor reply ("Hard Rock") to a canonical candidate
// ("Hard Rock International") so the per-vendor queue removes the right one.
function matchVendor(raw, candidates) {
  const list = Array.isArray(candidates) ? candidates.filter(Boolean) : [];
  if (!list.length || !raw) return null;
  const needle = String(raw).toLowerCase().trim();
  const exact = list.find((v) => String(v).toLowerCase().trim() === needle);
  if (exact) return exact;
  return list.find((v) => {
    const c = String(v).toLowerCase();
    return c.includes(needle) || needle.includes(c);
  }) || null;
}

/**
 * After a per-vendor order template is created, keep the "one vendor at a time"
 * loop going: a pending context whose remaining candidates are offered next.
 * Returns null when nothing is left to do.
 */
export function buildNextVendorPending(vendorCandidates) {
  const candidates = (vendorCandidates || []).filter(Boolean);
  if (!candidates.length) return null;
  return {
    kind: 'pending_setup_action',
    route: 'template_create',
    template_type: 'ordering',
    awaiting: 'vendor_choice',
    vendorCandidates: candidates,
    created_at: nowIso(),
    expires_at: plusMs(PENDING_TTL_MS),
  };
}

/**
 * Quick-reply chips for a "which vendor?" prompt: one per candidate vendor, plus
 * an opt-out that reads as a cancel.
 */
export function vendorChoiceChips(vendorCandidates) {
  const candidates = (vendorCandidates || []).filter(Boolean);
  return [
    ...candidates.map((v) => ({ key: `vendor:${v}`, label: v })),
    { key: 'done', label: 'No thanks — I’m all set' },
  ];
}

// ── Template format conversion (ordering ⇄ End of Period) ────────────────────
// When Counter Intelligence is asked to change a template's FORMAT (or the user
// taps "Transition to order template" in the editor), it must first ask whether
// to convert the existing template in place or make a copy in the other format.
// That choice is held in a pending context and resolved deterministically here.
const CONVERT_CANCEL_RE = CANCEL_RE;
const CONVERT_COPY_RE = /\b(copy|duplicate|make a copy|new (one|template|version|copy)|a copy|second|keep both|both|separate|version)\b/i;
const CONVERT_INPLACE_RE = /\b(convert|in[- ]?place|replace|change (it|this|the)|turn (it|this)|same (one|template)|this one|just (convert|change|do)|transform|existing)\b/i;

/**
 * Pending context for the convert-or-copy question. `currentFormat`/`targetFormat`
 * are 'ordering' | 'end_of_period'. `templateId` may be null when the user named a
 * template that isn't open — the backend resolves it from `command` at preview.
 */
export function buildPendingTemplateConvert({ templateId = null, templateName = '', currentFormat = null, targetFormat = null, command = '' } = {}) {
  return {
    kind: 'pending_template_convert',
    route: 'template_convert',
    awaiting: 'convert_mode',
    templateId: templateId ?? null,
    templateName: templateName || 'this template',
    currentFormat: currentFormat || null,
    targetFormat: targetFormat || null,
    command: command || '',
    created_at: nowIso(),
    expires_at: plusMs(PENDING_TTL_MS),
  };
}

/** Quick-reply chips for the convert-or-copy question. */
export function convertModeChips() {
  return [
    { key: 'convert_in_place', label: 'Convert this template' },
    { key: 'make_copy', label: 'Make a copy in the other format' },
    { key: 'cancel', label: 'Never mind' },
  ];
}

/**
 * Resolve a reply to the convert-or-copy question.
 * Returns 'convert_in_place' | 'make_copy' | 'cancel' | null (unrecognized).
 */
export function convertModeFromReply(text) {
  const t = String(text || '');
  if (CONVERT_CANCEL_RE.test(t)) return 'cancel';
  // Check copy first: "make a copy" also contains words the convert regex matches.
  if (CONVERT_COPY_RE.test(t)) return 'make_copy';
  if (CONVERT_INPLACE_RE.test(t)) return 'convert_in_place';
  return null;
}

/**
 * Turn the resolved pending context into a complete template-create command that
 * the existing template-preview endpoint understands.
 */
export function buildEffectiveCommand(pending, opts = {}) {
  const type = opts.templateType || (pending && pending.template_type) || 'ordering';
  if (type === 'end_of_period') {
    return 'Create an End of Period template for all active inventory items.';
  }
  const vendor = opts.vendor || (pending && pending.vendor);
  if (vendor) return `Create an ordering template for everything from ${vendor}.`;
  if (opts.category) return `Create an ordering template with all the ${opts.category}.`;
  if (opts.allItems) return 'Create an ordering template with all active inventory items.';
  let cmd = `Create an ordering template based on this request: ${(pending && pending.original_user_message) || 'all active inventory items'}`;
  if (opts.grouping === 'combined') cmd += '. Create one combined template.';
  return cmd.replace(/\s+/g, ' ').trim();
}

/**
 * Resolve a follow-up message against the pending context.
 * Returns one of:
 *   { action: 'cancel', reply }
 *   { action: 'ask', reply, pending }              — needs one more answer
 *   { action: 'create', effectiveCommand }         — go straight to the preview
 *   { action: 'passthrough' }                      — not a follow-up; classify normally
 */
export function resolveFollowup(text, pending, now = Date.now()) {
  if (!pending || isExpired(pending, now)) return { action: 'passthrough' };
  const t = String(text || '');
  if (isCancel(t)) return { action: 'cancel', reply: 'Okay — cancelled. Just ask whenever you want to pick this back up.' };

  // We already asked which vendor to start with — treat this reply as the vendor
  // name and build that one ordering template. When we captured a candidate list
  // (vendors that still need an order template), resolve a loose reply ("Hard
  // Rock") to the canonical vendor so the next-vendor queue stays accurate.
  if (pending.awaiting === 'vendor_choice') {
    const raw = t.trim();
    if (!raw) return { action: 'passthrough' };
    // A how-to / question ("how do I update prices?") isn't a vendor name — let
    // it re-classify instead of building an order template for that text.
    if (PIVOT_RE.test(raw)) return { action: 'passthrough' };
    const vendor = matchVendor(raw, pending.vendorCandidates) || raw;
    return {
      action: 'create',
      effectiveCommand: buildEffectiveCommand(pending, { vendor, templateType: 'ordering' }),
      createdVendor: vendor,
    };
  }

  // We asked which category — build an ordering template for that category.
  if (pending.awaiting === 'category_choice') {
    const category = t.trim();
    if (!category) return { action: 'passthrough' };
    return { action: 'create', effectiveCommand: buildEffectiveCommand(pending, { category, templateType: 'ordering' }) };
  }

  // We asked how to divide the order. Route the choice; a typed name is taken as
  // a direct scope (a vendor or category).
  if (pending.awaiting === 'order_division') {
    const reply = t.trim();
    if (BY_VENDOR_RE.test(reply)) return askVendor(pending);
    if (BY_CATEGORY_RE.test(reply)) return askCategory(pending);
    if (ALL_IN_ONE_RE.test(reply) || ALL_ITEMS_ONLY_RE.test(reply)) {
      return { action: 'create', effectiveCommand: buildEffectiveCommand(pending, { allItems: true, templateType: 'ordering' }) };
    }
    if (!reply) return { action: 'passthrough' };
    return { action: 'create', effectiveCommand: `Create an ordering template for ${reply}.` };
  }

  // The create executor asked for more detail (item scope / type / red-or-white).
  // Accumulate this reply onto the create command and try the preview again,
  // rather than re-classifying the bare answer ("Everything from Hard Rock
  // International") as a fresh — and now intent-less — request.
  if (pending.awaiting === 'item_scope') {
    const reply = t.trim();
    if (!reply) return { action: 'passthrough' };
    // A topic pivot ("how do I add a user?") isn't an item scope — re-classify it.
    if (PIVOT_RE.test(reply)) return { action: 'passthrough' };
    // For ordering templates, steer the division instead of building "all items".
    if (isOrderingPending(pending)) {
      if (BY_VENDOR_RE.test(reply)) return askVendor(pending);
      if (BY_CATEGORY_RE.test(reply)) return askCategory(pending);
      if (ALL_ITEMS_ONLY_RE.test(reply)) return askDivision(pending);
    }
    const base = (pending.accumulated_command || pending.original_user_message || 'Create a template').trim();
    const effectiveCommand = `${base}. ${reply}`.replace(/\s+/g, ' ').trim();
    return { action: 'create', effectiveCommand };
  }

  const grouping = COMBINED_RE.test(t) ? 'combined' : (SEPARATE_RE.test(t) ? 'separate_by_vendor' : null);
  let templateType = null;
  if (EOP_RE.test(t)) templateType = 'end_of_period';
  else if (ORDERING_RE.test(t)) templateType = 'ordering';
  const affirm = AFFIRM_RE.test(t);

  // "Separate by vendor" — not multi-creating yet: ask which vendor, keep the
  // constraint. Drop the grouping chips; the next reply is a free-text vendor.
  if (grouping === 'separate_by_vendor') {
    return {
      action: 'ask',
      reply: 'I can start by creating one vendor order template at a time. Which vendor should I start with?',
      pending: {
        ...pending,
        awaiting: 'vendor_choice',
        grouping: 'separate_by_vendor',
        choices: undefined,
        expires_at: plusMs(PENDING_TTL_MS),
      },
    };
  }

  // Confirming an ORDERING template with no vendor/scope in mind would otherwise
  // dump the whole catalogue, which is rarely what anyone wants. Ask how to
  // divide it first (by vendor / by category / all in one). End of Period and
  // vendor-specific pendings skip this — "all items" is the right default there.
  const resolvingType = templateType || (pending && pending.template_type);
  if (pending.awaiting === 'confirmation'
      && resolvingType !== 'end_of_period'
      && !pending.vendor
      && grouping !== 'combined'
      && (affirm || templateType === 'ordering')) {
    return askDivision(pending);
  }

  // Any clear confirmation / narrowing answer resolves straight to the preview.
  if (affirm || grouping === 'combined' || templateType) {
    return { action: 'create', effectiveCommand: buildEffectiveCommand(pending, { grouping, templateType }) };
  }

  // Not recognizable as an answer to the pending question — classify normally.
  return { action: 'passthrough' };
}
